import React, { useEffect, useMemo, useState, useCallback } from "react";
import "./ModIndex.css";
import { ReactComponent as Undo } from "../../../../../icons/undo.svg";
import ModFilter from "../ModFilters/ModFilter";

type Row = {
  generator: string;
  vc: number | string;
  time_slot__slot: string;
  time_string: string;
  total_capacity?: number;
  must_run?: number;
  margin_buy?: number;
  margin_conservative_sell?: number;
  margin_aggressive_sell?: number;
  [k: string]: any;
};

interface ModTableProps {
  maxHeight?: string;
  mode?: "normal" | "edit";
  data: any[];
  setModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  handleSubmit?: (data: any) => void;
  loading?: boolean;
  setModalFilter?: React.Dispatch<React.SetStateAction<boolean>>;
  handleUpdateReview?: (data: any) => void;
  modalFilter?: boolean;
  uniqueGeneratorType?: any[];
  uniqueOwnership?: any[];
  uniqueBlock?: any[];
}

interface EditingCell {
  gen: string;
  slot: string;
  field: string;
}

const editableFields = [
  "total_capacity",
  "available",
  "must_run",
  "margin_buy",
  "margin_conservative_sell",
  "margin_aggressive_sell",
];

const VC_FIELDS = ["vc"];

export function ModTable({
  maxHeight = "calc(-300px + 100vh)",
  mode = "normal",
  data,
  setModalOpen,
  handleSubmit,
  loading = false,
  setModalFilter,
  handleUpdateReview,
  modalFilter,
  uniqueGeneratorType,
  uniqueOwnership,
  uniqueBlock,
}: ModTableProps) {
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [editingCell, setEditingCell] = useState<EditingCell | null>(null);
  const [localValues, setLocalValues] = useState<Record<string, any>>({});
  const [draftValue, setDraftValue] = useState<string>("");

  // Preprocess data once: slots (ordered), grouped rows per generator, row lookup (gen->slot->row)
  const preprocessed = useMemo(() => {
    const grouped = new Map<string, Row[]>();
    const rowLookup = new Map<string, Map<string, Row>>();
    const slotsOrdered: string[] = [];
    const timeSlot: string[] = [];
    const seenSlots = new Set<string>();

    for (const r of data) {
      // group
      if (!grouped.has(r.generator)) grouped.set(r.generator, []);
      grouped.get(r.generator)!.push(r);
      // slot order
      if (!seenSlots.has(r.time_slot__slot)) {
        seenSlots.add(r.time_slot__slot);
        slotsOrdered.push(r.time_slot__slot);
        timeSlot.push(r.time_string)
      }
      // row lookup
      if (!rowLookup.has(r.generator)) rowLookup.set(r.generator, new Map());
      rowLookup.get(r.generator)!.set(r.time_slot__slot, r);
    }

    return { grouped, rowLookup, slots: slotsOrdered,timeSlot };
  }, [data]);

  const { grouped, rowLookup, slots,timeSlot } = preprocessed;
  const generators = useMemo(() => Array.from(grouped.keys()), [grouped]);

  const valueKey = useCallback(
    (gen: string, slot: string, field: string) => `${gen}__${slot}__${field}`,
    []
  );
  const parseValueKey = useCallback((k: string) => {
    const [gen, slot, field] = k.split("__");
    return { gen, slot, field };
  }, []);

  const toggleVCSort = useCallback(
    () => setSortOrder((o) => (o === "asc" ? "desc" : "asc")),
    []
  );

  const sortedGenerators = useMemo(() => {
    if (generators.length <= 1) return generators;
    return [...generators].sort((a, b) => {
      const aVc = grouped.get(a)?.[0]?.vc;
      const bVc = grouped.get(b)?.[0]?.vc;
      const av = Number(aVc) || 0;
      const bv = Number(bVc) || 0;
      return sortOrder === "asc" ? av - bv : bv - av;
    });
  }, [generators, grouped, sortOrder]);

  const findOriginalRow = useCallback(
    (gen: string, slot: string) => rowLookup.get(gen)?.get(slot),
    [rowLookup]
  );

  const startEdit = (
    gen: string,
    slot: string,
    field: string,
    original: any
  ) => {
    if (mode !== "edit") return;
    if (
      editingCell &&
      editingCell.gen === gen &&
      editingCell.slot === slot &&
      editingCell.field === field
    )
      return;
    const k = valueKey(gen, slot, field);
    const currentValue =
      localValues[k] !== undefined ? localValues[k] : original;
    setDraftValue(
      currentValue === "-" || currentValue == null ? "" : String(currentValue)
    );
    setEditingCell({ gen, slot, field });
  };

  const getOriginalValue = useCallback(
    (gen: string, slot: string, field: string) => {
      const r = findOriginalRow(gen, slot);
      const v = r ? (r as any)[field] : undefined;
      return v === undefined || v === null ? "-" : v;
    },
    [findOriginalRow]
  );

  const confirmEdit = () => {
    if (!editingCell) return;
    const { gen, slot, field } = editingCell;
    const k = valueKey(gen, slot, field);
    const trimmed = draftValue.trim();
    const valueToSave =
      trimmed === "" ? "" : isNaN(Number(trimmed)) ? trimmed : Number(trimmed);

    const original = getOriginalValue(gen, slot, field);
    const prev = Object.prototype.hasOwnProperty.call(localValues, k)
      ? localValues[k]
      : original;
    if (valueToSave === prev) {
      setEditingCell(null);
      setDraftValue("");
      return;
    }
    setLocalValues((p) => ({ ...p, [k]: valueToSave }));
    setEditingCell(null);
    setDraftValue("");
  };

  const cancelEdit = () => {
    setEditingCell(null);
    setDraftValue("");
  };

  const revertCell = (gen: string, slot: string, field: string) => {
    const k = valueKey(gen, slot, field);
    setLocalValues((p) => {
      if (!(k in p)) return p;
      const { [k]: _, ...rest } = p;
      return rest;
    });
  };

  const revertAll = () => {
    if (!Object.keys(localValues).length) return;
    setLocalValues({});
    cancelEdit();
  };

  // Effective data (overlay local edits)
  const effectiveData = useMemo(() => {
    const changedKeys = Object.keys(localValues);
    if (!changedKeys.length) return data;
    const map = new Map<string, any>();
    data.forEach((r) =>
      map.set(`${r.generator}__${r.time_slot__slot}`, { ...r })
    );
    for (const k of changedKeys) {
      const { gen, slot, field } = parseValueKey(k);
      const rk = `${gen}__${slot}`;
      if (!map.has(rk)) map.set(rk, { generator: gen, time_slot__slot: slot });
      map.get(rk)![field] = localValues[k];
    }
    return Array.from(map.values());
  }, [data, localValues, parseValueKey]);

  // New handleUpdateClick that logs full changed rows (all keys) + entire dataset snapshot
  const handleUpdateClick = () => {
    // Identify which (gen,slot) pairs changed
    const changedCompounds = new Set<string>();
    for (const k of Object.keys(localValues)) {
      const { gen, slot } = parseValueKey(k);
      changedCompounds.add(`${gen}__${slot}`);
    }

    // Full objects for just the changed rows (every key preserved)
    const fullChangedRows = effectiveData.filter((r) =>
      changedCompounds.has(`${r.generator}__${r.time_slot__slot}`)
    );

    handleSubmit?.(fullChangedRows);
  };

  const applyBulkChanges = useCallback(
    (changedRows: any[]) => {
      if (!Array.isArray(changedRows) || !changedRows.length) return;
      setLocalValues((prev) => {
        const next = { ...prev };
        for (const r of changedRows) {
          const gen = r.generator;
          const slot = r.time_slot__slot;
          const original = findOriginalRow(gen, slot);
          [...editableFields, ...VC_FIELDS].forEach((f) => {
            if (!(f in r)) return;
            const k = valueKey(gen, slot, f);
            const orig = original ? original[f] : undefined;
            if (r[f] !== orig) next[k] = r[f];
            else if (k in next) delete next[k];
          });
        }
        return next;
      });
    },
    [findOriginalRow, valueKey]
  );

  // Precompute VC overrides per generator
  const vcOverrides = useMemo(() => {
    const map = new Map<
      string,
      { value: any; differs: boolean; keys: string[]; original: any }
    >();
    for (const gen of generators) {
      const rows = grouped.get(gen);
      if (!rows || !rows.length) continue;
      const original = rows[0].vc;
      const keys: string[] = [];
      let overrideVal: any = undefined;
      let differs = false;
      for (const r of rows) {
        const k = valueKey(gen, r.time_slot__slot, "vc");
        if (localValues[k] !== undefined) {
          keys.push(k);
          if (overrideVal === undefined) overrideVal = localValues[k];
        }
      }
      if (!keys.length) {
        map.set(gen, { value: original, differs: false, keys: [], original });
      } else {
        differs = overrideVal !== original;
        map.set(gen, {
          value: overrideVal,
          differs,
          keys,
          original,
        });
      }
    }
    return map;
  }, [generators, grouped, localValues, valueKey]);

  const revertVc = (vcKeys: string[]) => {
    if (!vcKeys.length) return;
    setLocalValues((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const k of vcKeys) {
        if (k in next) {
          delete next[k];
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  };

  useEffect(() => {
    setEditingCell(null);
  }, [data]);

  if (!data || !data.length) {
    return (
      <div className="w-full h-full text-center flex justify-center items-center text-sm mt-10 text-gray-500">
        No data available
      </div>
    );
  }

  const localSize = Object.keys(localValues).length;
  const isEditMode = mode === "edit";

  return (
    <div
      className="relative overflow-auto capitalize text-nowrap rounded-lg border border-gray-200"
      style={{ maxHeight }}
    >
      <table id="my-table" className="table-auto mod_table mb-0">
        <thead className="sticky top-0 z-50 text-[#F1935C] bg-[#FFF8F4]">
          <tr>
            <th
              colSpan={2}
              className="sticky left-0 z-30 w-[243px] border-[#F1935C] px-4 py-2 text-center rounded-tl-xl "
            >
              Generators
            </th>
            {slots.map((slot,index) => (
              <th
                key={slot}
                colSpan={5}
                className="border border-[#F1935C] text-[#34656D] px-4 py-2 text-center "
              >
                Slot {slot}: ({timeSlot[index]})
              </th>
            ))}
          </tr>
          <tr>
            <th className="sticky left-0 top-0 z-50 min-w-[242px] border-r border-[#F1935C] px-4 py-2 text-center !border-b-0 ">
              Name
            </th>
            <th className="sticky left-[242px] w-[55px] z-40 border border-[#F1935C] px-4 py-2 text-center ">
              <button
                type="button"
                onClick={toggleVCSort}
                className="sortable cursor-pointer select-none flex items-center justify-center gap-1"
              >
                VC {sortOrder === "asc" ? "▲" : "▼"}
              </button>
            </th>
            {slots.map((slot) => (
              <React.Fragment key={slot}>
                <th className="border border-[#F1935C] px-4 py-2 text-center">
                  DC
                </th>
                <th className="border border-[#F1935C] px-4 py-2 text-center">
                  MR
                </th>
                <th className="border border-[#F1935C] px-4 py-2 text-center">
                  BM
                </th>
                <th className="border border-[#F1935C] px-4 py-2 text-center">
                  SM
                </th>
                <th className="border border-[#F1935C] px-4 py-2 text-center">
                  SAM
                </th>
              </React.Fragment>
            ))}
          </tr>
        </thead>
        <tbody className="text-[#8C8A96] font-normal text-[13px]">
          {sortedGenerators.map((gen) => {
            const rowsForGen = grouped.get(gen)!;
            const vcInfo = vcOverrides.get(gen);
            return (
              <tr
                key={gen}
                className="border-b border-gray-200 hover:bg-gray-100 text-center"
              >
                <td className="sticky left-0 z-10 w-[242px] bg-white py-3 px-4">
                  {gen}
                </td>
                <td className="sticky left-[242px] w-[55px] z-10 bg-white py-3 px-4 !border-r !border-[#F1935C]">
                  <div className="relative flex items-center justify-end pr-4">
                    <span
                      className={
                        vcInfo?.differs ? "text-primary font-semibold" : ""
                      }
                    >
                      {vcInfo?.value ?? rowsForGen[0].vc}
                    </span>
                    {vcInfo?.differs && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          revertVc(vcInfo.keys);
                        }}
                        className="absolute top-2 -right-2 bg-amber-500 hover:bg-amber-600 text-white rounded text-[10px] px-2 py-[2px] leading-tight shadow"
                        title="Revert VC"
                      >
                        ↺
                      </button>
                    )}
                  </div>
                </td>
                {slots.map((slot, slotIdx) => {
                  const row = rowLookup.get(gen)?.get(slot);
                  const cells: { field: string; value: any }[] = [
                    {
                      field: "total_capacity",
                      value: row?.total_capacity ?? "-",
                    },
                    { field: "must_run", value: row?.must_run ?? "-" },
                    { field: "margin_buy", value: row?.margin_buy ?? "-" },
                    {
                      field: "margin_conservative_sell",
                      value: row?.margin_conservative_sell ?? "-",
                    },
                    {
                      field: "margin_aggressive_sell",
                      value: row?.margin_aggressive_sell ?? "-",
                    },
                  ];
                  const baseColor =
                    slotIdx % 2 === 0 ? "bg-blue-50" : "bg-white";
                  return cells.map((c, i) => {
                    const k = valueKey(gen, slot, c.field);
                    const isEditing =
                      editingCell &&
                      editingCell.gen === gen &&
                      editingCell.slot === slot &&
                      editingCell.field === c.field;
                    const display =
                      localValues[k] !== undefined ? localValues[k] : c.value;
                    const differs =
                      Object.prototype.hasOwnProperty.call(localValues, k) &&
                      localValues[k] !== c.value;
                    const edge =
                      i === 4
                        ? "!border-r !border-[#F1935C]"
                        : i === cells.length - 1
                        ? ""
                        : "";
                    return (
                      <td
                        key={c.field}
                        className={`${baseColor} relative py-3 px-2 ${edge} cursor-${
                          isEditMode ? "pointer" : "default"
                        }`}
                        onClick={() => {
                          if (isEditMode && !isEditing)
                            startEdit(gen, slot, c.field, c.value);
                        }}
                        data-edited={differs ? "true" : "false"}
                      >
                        {differs && !isEditing && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              revertCell(gen, slot, c.field);
                            }}
                            className="absolute top-[30px] z-[9]  bg-amber-500 hover:bg-amber-600 text-white rounded text-[10px] px-2 py-[2px] leading-tight shadow"
                            title="Revert this cell"
                          >
                            ↺
                          </button>
                        )}
                        {!isEditing && (
                          <span
                            className={
                              differs ? "text-primary font-semibold" : ""
                            }
                          >
                            {display}
                          </span>
                        )}
                        {isEditing && (
                          <div className="absolute z-[9999] inset-0 flex items-center justify-center">
                            <div className="flex border border-gray-300 shadow-lg bg-white">
                            <input
                              autoFocus
                              step="any"
                              inputMode="decimal"
                              type="number"
                              className="w-24 h-8 bg-[#f8f7f6] font-medium text-right pr-2 pl-2 py-1 focus:outline-none border-0"
                              value={draftValue}
                              onChange={(e) => setDraftValue(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") confirmEdit();
                                else if (e.key === "Escape") cancelEdit();
                              }}
                              onBlur={(e) => {
                                if (
                                  editingCell &&
                                  !e.currentTarget.contains(
                                    e.relatedTarget as Node
                                  )
                                ) {
                                  cancelEdit();
                                }
                              }}
                            />
                            <button
                              type="button"
                              tabIndex={-1}
                              onMouseDown={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                confirmEdit();
                              }}
                              className="w-8 h-8 bg-green-500 hover:bg-green-600 text-white text-sm flex items-center justify-center border-0"
                              title="Save"
                            >
                              ✓
                            </button>
                          </div>
                          </div>
                        )}
                      </td>
                    );
                  });
                })}
              </tr>
            );
          })}
        </tbody>
      </table>

      {isEditMode && (
        <div className="sticky bottom-0 left-0 right-0 z-[80] bg-white border-t px-4 py-3 flex gap-x-6 justify-between items-center">
          <div>
            <button
              onClick={revertAll}
              className="flex items-center gap-2 px-4 py-3 text-base text-[#34656D] font-medium"
            >
              <Undo className="w-6 h-6" />
              Revert All
            </button>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setModalOpen(false)}
              className="flex items-center gap-2 px-4 py-2 text-sm rounded-xl border-2 border-[#8C8A96] text-[#8C8A96]"
            >
              Cancel
            </button>
            <button
              onClick={handleUpdateClick}
              className="flex items-center gap-2 p-3 text-sm rounded-xl disabled:text-[#A9A8B1] disabled:bg-[#E2E2E5] bg-[#F1935C] text-white font-medium"
              disabled={localSize === 0 || loading}
            >
              {loading ? "Saving..." : "Update & Review"}
            </button>
          </div>
        </div>
      )}

      <ModFilter
        loading={loading}
        uniqueGeneratorTypes={uniqueGeneratorType}
        uniqueOwnership={uniqueOwnership}
        uniqueBlocks={uniqueBlock}
        isOpen={modalFilter}
        handleSubmit={handleUpdateReview}
        onLocalApply={applyBulkChanges}
        onClose={() => (setModalFilter ? setModalFilter(false) : null)}
        data={effectiveData}
      />
    </div>
  );
}
